// src/services/servicioAutenticacion.js (cliente)
import api from './api';
import store from '@/store';
import sessionManager from './sessionManager';
import { esSubdominioLogin, construirUrlLogout } from '@/utils/subdominio';

const CLAVE_TOKEN_ACCESO = 'token_acceso_cliente';
const CLAVE_TOKEN_REFRESCO = 'token_refresco_cliente';
const CLAVE_MODULOS = 'modulos_cliente';
const CLAVE_PERMISOS = 'permisos_cliente';
const CLAVE_USUARIO = 'usuario_cliente';
const CLAVE_SUSCRIPCION = 'suscripcion_cliente';

const CLAVE_TOKEN_ACCESO_LEGADO = 'client_access_token';
const CLAVE_TOKEN_REFRESCO_LEGADO = 'client_refresh_token';
const CLAVE_MODULOS_LEGADO = 'client_modules';
const CLAVE_USUARIO_LEGADO = 'client_user';
const CLAVE_SUSCRIPCION_LEGADO = 'client_subscription';

function migrarLegado() {
  if (!localStorage.getItem(CLAVE_TOKEN_ACCESO) && localStorage.getItem(CLAVE_TOKEN_ACCESO_LEGADO)) {
    localStorage.setItem(CLAVE_TOKEN_ACCESO, localStorage.getItem(CLAVE_TOKEN_ACCESO_LEGADO));
  }
  if (!localStorage.getItem(CLAVE_TOKEN_REFRESCO) && localStorage.getItem(CLAVE_TOKEN_REFRESCO_LEGADO)) {
    localStorage.setItem(CLAVE_TOKEN_REFRESCO, localStorage.getItem(CLAVE_TOKEN_REFRESCO_LEGADO));
  }
  if (!localStorage.getItem(CLAVE_MODULOS) && localStorage.getItem(CLAVE_MODULOS_LEGADO)) {
    localStorage.setItem(CLAVE_MODULOS, localStorage.getItem(CLAVE_MODULOS_LEGADO));
  }
  if (!localStorage.getItem(CLAVE_USUARIO) && localStorage.getItem(CLAVE_USUARIO_LEGADO)) {
    localStorage.setItem(CLAVE_USUARIO, localStorage.getItem(CLAVE_USUARIO_LEGADO));
  }
  if (!localStorage.getItem(CLAVE_SUSCRIPCION) && localStorage.getItem(CLAVE_SUSCRIPCION_LEGADO)) {
    localStorage.setItem(CLAVE_SUSCRIPCION, localStorage.getItem(CLAVE_SUSCRIPCION_LEGADO));
  }
}

export async function iniciarSesion(correo, clave) {
  migrarLegado();
  localStorage.removeItem(CLAVE_TOKEN_ACCESO);
  const { data } = await api.post('/autenticacion/cliente/iniciar', { correo, clave });
  const responseData = data?.data;
  const access = responseData?.access_token;
  const tokenRefresco = responseData?.refresh_token;
  if (access) {
    localStorage.setItem(CLAVE_TOKEN_ACCESO, access);
  }
  if (tokenRefresco) localStorage.setItem(CLAVE_TOKEN_REFRESCO, tokenRefresco);
  if (Array.isArray(responseData?.modules)) {
    localStorage.setItem(CLAVE_MODULOS, JSON.stringify(responseData.modules));
    store.commit('setModules', responseData.modules);
  } else {
    localStorage.removeItem(CLAVE_MODULOS);
    store.commit('setModules', []);
  }
  if (Array.isArray(responseData?.permisos)) {
    localStorage.setItem(CLAVE_PERMISOS, JSON.stringify(responseData.permisos));
    store.commit('setPermisos', responseData.permisos);
  } else {
    localStorage.removeItem(CLAVE_PERMISOS);
    store.commit('setPermisos', []);
  }
  if (responseData?.user) {
    localStorage.setItem(CLAVE_USUARIO, JSON.stringify(responseData.user));
    store.commit('setUser', responseData.user);
  } else {
    localStorage.removeItem(CLAVE_USUARIO);
    store.commit('setUser', null);
  }
  if (responseData?.subscription) {
    localStorage.setItem(CLAVE_SUSCRIPCION, JSON.stringify(responseData.subscription));
    store.commit('setSubscription', responseData.subscription);
  } else {
    localStorage.removeItem(CLAVE_SUSCRIPCION);
    store.commit('setSubscription', null);
  }

  // Cargar permisos efectivos del nuevo sistema de control de acceso
  // (sobrescribe modules/permisos legacy con la fuente canonica).
  try {
    await store.dispatch('cargarPermisosEfectivos');
  } catch (e) {
    console.warn('[auth] No se pudieron cargar permisos efectivos tras login', e);
  }

  // Iniciar monitoreo de sesión después del inicio exitoso
  sessionManager.startMonitoring(
    () => {
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('session-warning'));
      }
    },
    () => {
      cerrarSesion();
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('session-expired'));
      }
    }
  );

  return data;
}

export function cerrarSesion() {
  sessionManager.stopMonitoring();

  localStorage.removeItem(CLAVE_TOKEN_ACCESO);
  localStorage.removeItem(CLAVE_TOKEN_REFRESCO);
  localStorage.removeItem(CLAVE_MODULOS);
  localStorage.removeItem(CLAVE_PERMISOS);
  localStorage.removeItem(CLAVE_USUARIO);
  localStorage.removeItem(CLAVE_SUSCRIPCION);

  localStorage.removeItem(CLAVE_TOKEN_ACCESO_LEGADO);
  localStorage.removeItem(CLAVE_TOKEN_REFRESCO_LEGADO);
  localStorage.removeItem(CLAVE_MODULOS_LEGADO);
  localStorage.removeItem(CLAVE_USUARIO_LEGADO);
  localStorage.removeItem(CLAVE_SUSCRIPCION_LEGADO);

  store.commit('clearAuth');

  // Si estamos en un subdominio de empresa, redirigir directamente a app.sicecom.com
  // con ?accion=cerrar-sesion para que limpie también su propio localStorage.
  // Usar window.location.replace para que el componente actual desaparezca de inmediato.
  if (!esSubdominioLogin()) {
    const urlLogout = construirUrlLogout()
    if (urlLogout) {
      window.location.replace(urlLogout)
      return
    }
  }
}

export async function refrescarSesion() {
  migrarLegado();
  const rt = localStorage.getItem(CLAVE_TOKEN_REFRESCO);
  if (!rt) throw new Error('No hay token de refresco');
  const { data } = await api.post('/autenticacion/cliente/refrescar', { refresh_token: rt });
  const responseData = data?.data;
  const access = responseData?.access_token;
  if (access) localStorage.setItem(CLAVE_TOKEN_ACCESO, access);

  // Sincronizar módulos y permisos actualizados en el refresh
  if (Array.isArray(responseData?.modules)) {
    localStorage.setItem(CLAVE_MODULOS, JSON.stringify(responseData.modules));
    store.commit('setModules', responseData.modules);
  }
  if (Array.isArray(responseData?.permisos)) {
    localStorage.setItem(CLAVE_PERMISOS, JSON.stringify(responseData.permisos));
    store.commit('setPermisos', responseData.permisos);
  }

  sessionManager.resetTimers();
  return data;
}

export async function obtenerPerfil() {
  const { data } = await api.get('/autenticacion/cliente/perfil');
  // Los módulos se gestionan exclusivamente por cargarPermisosEfectivos (nuevo sistema).
  // No se sobreescribe setModules aquí para evitar resetear el estado con datos legacy.
  if (data?.user) {
    localStorage.setItem(CLAVE_USUARIO, JSON.stringify(data.user));
    store.commit('setUser', data.user);
  } else {
    localStorage.removeItem(CLAVE_USUARIO);
    store.commit('setUser', null);
  }
  if (data?.subscription) {
    localStorage.setItem(CLAVE_SUSCRIPCION, JSON.stringify(data.subscription));
    store.commit('setSubscription', data.subscription);
  } else {
    localStorage.removeItem(CLAVE_SUSCRIPCION);
    store.commit('setSubscription', null);
  }
  return data;
}

export async function recuperarClave(email) {
  const correo = String(email || '').trim().toLowerCase();
  const { data } = await api.post('/clientes/recuperar-clave', { correo });
  return data;
}

export async function solicitarRecuperacion(ruc, correo) {
  const payload = {
    ruc: String(ruc || '').trim(),
    correo: String(correo || '').trim().toLowerCase()
  };
  const { data } = await api.post('/clientes/recuperar-clave', payload);
  return data;
}

export async function validarCodigoRecuperacion(token, codigo) {
  const payload = {
    token: String(token || '').trim(),
    codigo: String(codigo || '').trim()
  };
  const { data } = await api.post('/clientes/recuperar-clave/validar', payload);
  return data;
}

export async function cambiarClaveRecuperacion(token, nuevaClave, confirmarClave) {
  const payload = {
    token: String(token || '').trim(),
    nueva_clave: String(nuevaClave || ''),
    confirmar_clave: String(confirmarClave || '')
  };
  const { data } = await api.post('/clientes/recuperar-clave/cambiar', payload);
  return data;
}

export async function activarCuenta(token, nuevaClave, confirmarClave) {
  const payload = {
    token: String(token || '').trim(),
    nueva_clave: String(nuevaClave || ''),
    confirmar_clave: String(confirmarClave || '')
  };
  const { data } = await api.post('/clientes/activar-cuenta', payload);
  return data;
}

function decodificarJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function estaAutenticado() {
  migrarLegado();
  const t = localStorage.getItem(CLAVE_TOKEN_ACCESO);
  if (!t) return false;

  const p = decodificarJwt(t);
  if (!p) return false;

  if (!p.exp) return true;

  return (p.exp * 1000) > Date.now();
}

export function obtenerTokenAcceso() {
  migrarLegado();
  return localStorage.getItem(CLAVE_TOKEN_ACCESO);
}

export default {
  iniciarSesion,
  cerrarSesion,
  refrescarSesion,
  obtenerPerfil,
  recuperarClave,
  solicitarRecuperacion,
  validarCodigoRecuperacion,
  cambiarClaveRecuperacion,
  activarCuenta,
  estaAutenticado,
  obtenerTokenAcceso
};

